/**
 * Media Management API Service
 */

import { apiClient } from '../../../core/config/api';
import type {
  MediaSearchParams,
  MediaSearchResponse,
  ContextAssetsParams,
  MediaUploadRequest,
  MediaUploadResponse,
  MediaUsageCreateRequest,
  MediaUsageResponse,
  MediaAssetDetail,
  MediaAssetSummary,
  MediaSourceMetadata,
  FileValidationResult
} from '../types/media';
import { ACCEPT_BY_KIND, MAX_FILE_SIZE_MB, kindForFile } from '../utils/fileAccept';

// ============================================
// FILE VALIDATION UTILITIES
// ============================================

export const validateFile = (file: File): FileValidationResult => {
  const errors: string[] = [];
  const extension = '.' + file.name.split('.').pop()?.toLowerCase();

  const maxSizeBytes = MAX_FILE_SIZE_MB * 1024 * 1024;
  if (file.size > maxSizeBytes) {
    errors.push(`File size (${(file.size / 1024 / 1024).toFixed(1)}MB) exceeds maximum allowed size (${MAX_FILE_SIZE_MB}MB)`);
  }

  if (!kindForFile(file)) {
    const allowed = Object.values(ACCEPT_BY_KIND)
      .flatMap((spec) => spec.extensions.map((ext) => `.${ext}`))
      .join(', ');
    errors.push(`File type '${extension}' is not allowed. Allowed types: ${allowed}`);
  }

  return {
    isValid: errors.length === 0,
    errors,
    fileInfo: {
      size: file.size,
      type: file.type,
      extension
    }
  };
};

// ============================================
// MEDIA API SERVICE
// ============================================

export const mediaApiService = {
  // ============================================
  // MEDIA SEARCH AND RETRIEVAL
  // ============================================

  /**
   * Search media assets with filters and pagination
   */
  searchMedia: async (params: MediaSearchParams = {}): Promise<MediaSearchResponse> => {
    const searchParams = new URLSearchParams();
    
    // Add search parameters
    if (params.text_query) searchParams.append('q', params.text_query);
    if (params.media_types?.length) {
      searchParams.append('media_types', params.media_types.join(','));
    }
    if (params.source_types?.length) {
      searchParams.append('source_types', params.source_types.join(','));
    }
    if (params.subject_tags?.length) {
      searchParams.append('subject_tags', params.subject_tags.join(','));
    }
    if (params.grade_level_tags?.length) {
      searchParams.append('grade_level_tags', params.grade_level_tags.join(','));
    }
    if (params.concept_tags?.length) {
      searchParams.append('concept_tags', params.concept_tags.join(','));
    }
    if (params.context_tags?.length) {
      searchParams.append('context_tags', params.context_tags.join(','));
    }
    if (params.style_tags?.length) {
      searchParams.append('style_tags', params.style_tags.join(','));
    }
    if (params.access_level) {
      searchParams.append('access_level', params.access_level);
    }
    if (params.min_usage_count !== undefined) {
      searchParams.append('min_usage_count', params.min_usage_count.toString());
    }
    
    // Pagination
    searchParams.append('page', (params.page || 1).toString());
    searchParams.append('page_size', (params.page_size || 20).toString());
    
    // Sorting
    searchParams.append('sort_by', params.sort_by || 'created_at');
    searchParams.append('sort_order', params.sort_order || 'desc');
    
    const response = await apiClient.get(`/media/search?${searchParams.toString()}`);
    return response.data;
  },

  /**
   * Get media assets related to an entity context (self + direct children).
   * Returns assets ordered by relevance: entity's own media first, then children.
   */
  getContextAssets: async (params: ContextAssetsParams): Promise<MediaSearchResponse> => {
    const searchParams = new URLSearchParams();
    searchParams.append('entity_type', params.entity_type);
    searchParams.append('entity_id', params.entity_id);
    if (params.media_type) searchParams.append('media_type', params.media_type);
    searchParams.append('page', (params.page || 1).toString());
    searchParams.append('page_size', (params.page_size || 20).toString());

    const response = await apiClient.get(`/media/context-assets?${searchParams.toString()}`);
    return response.data;
  },

  /**
   * Get detailed media asset information
   */
  getMediaAsset: async (mediaAssetId: string): Promise<MediaAssetDetail> => {
    const response = await apiClient.get(`/media/${mediaAssetId}`);
    return response.data.media_asset;
  },

  /**
   * Download media file
   */
  downloadMedia: async (mediaAssetId: string): Promise<Blob> => {
    const response = await apiClient.get(`/media/${mediaAssetId}/download`, {
      responseType: 'blob'
    });
    return response.data;
  },

  /**
   * Mint the Cloud CDN media cookie for a logged-in browser. withCredentials
   * lets the browser store the cross-origin Set-Cookie. expires_at is null
   * when the backend has no signing key (dev, previews).
   */
  mintCdnCookie: async (): Promise<{ expires_at: string | null }> => {
    const response = await apiClient.post('/media/cdn-cookie', undefined, {
      withCredentials: true,
    });
    return response.data;
  },

  /**
   * Clear the Cloud CDN media cookie on sign-out.
   */
  clearCdnCookie: async (): Promise<void> => {
    await apiClient.delete('/media/cdn-cookie', { withCredentials: true });
  },

  // ============================================
  // MEDIA UPLOAD
  // ============================================

  /**
   * Upload media file with metadata
   */
  uploadMedia: async (request: MediaUploadRequest): Promise<MediaUploadResponse> => {
    // Validate file before upload
    const validation = validateFile(request.file);
    if (!validation.isValid) {
      throw new Error(`File validation failed: ${validation.errors.join(', ')}`);
    }

    // Create form data
    const formData = new FormData();
    formData.append('file', request.file);
    
    // Add metadata fields
    if (request.title) formData.append('title', request.title);
    if (request.description) formData.append('description', request.description);
    if (request.subject_tags?.length) {
      formData.append('subject_tags', request.subject_tags.join(','));
    }
    if (request.grade_level_tags?.length) {
      formData.append('grade_level_tags', request.grade_level_tags.join(','));
    }
    if (request.concept_tags?.length) {
      formData.append('concept_tags', request.concept_tags.join(','));
    }
    if (request.context_tags?.length) {
      formData.append('context_tags', request.context_tags.join(','));
    }
    if (request.style_tags?.length) {
      formData.append('style_tags', request.style_tags.join(','));
    }
    if (request.access_level) {
      formData.append('access_level', request.access_level);
    }
    if (request.license_type) {
      formData.append('license_type', request.license_type);
    }
    if (request.copyright_info) {
      formData.append('copyright_info', request.copyright_info);
    }
    if (request.used_in_entity_type) {
      formData.append('used_in_entity_type', request.used_in_entity_type);
    }
    if (request.used_in_entity_id) {
      formData.append('used_in_entity_id', request.used_in_entity_id);
    }

    const response = await apiClient.post('/media/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 30000, // 30 second timeout for file uploads
    });
    
    return response.data;
  },

  // ============================================
  // MEDIA USAGE MANAGEMENT
  // ============================================

  /**
   * Create media usage record
   */
  createMediaUsage: async (request: MediaUsageCreateRequest): Promise<MediaUsageResponse> => {
    const response = await apiClient.post(
      `/media/usage/in/${request.used_in_entity_type}/${request.used_in_entity_id}`,
      { media_asset_id: request.media_asset_id }
    );
    return response.data;
  },

  /**
   * Delete media usage record
   */
  deleteMediaUsage: async (mediaUsageId: string): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.delete(`/media/usage/${mediaUsageId}`);
    return response.data;
  },

  // ============================================
  // MEDIA ASSET MANAGEMENT
  // ============================================

  /**
   * Update media asset metadata
   */
  updateMediaAsset: async (
    mediaAssetId: string,
    updates: Partial<Pick<MediaAssetDetail, 'title' | 'description' | 'subject_tags' | 'grade_level_tags' | 'concept_tags' | 'context_tags' | 'style_tags' | 'access_level'>>
  ): Promise<MediaAssetDetail> => {
    const response = await apiClient.put(`/media/${mediaAssetId}`, updates);
    return response.data.media_asset;
  },

  /**
   * Create media asset from external URL (e.g. archival photos)
   */
  createMediaFromExternalUrl: async (request: {
    external_url: string;
    original_filename: string;
    title?: string;
    description?: string;
    attribution?: string;
    source_url?: string;
    subject_tags?: string[];
    grade_level_tags?: string[];
    concept_tags?: string[];
    context_tags?: string[];
    style_tags?: string[];
    access_level?: string;
    license_type?: string;
    media_metadata?: Record<string, any>;
    source_metadata?: MediaSourceMetadata;
    used_in_entity_type?: string;
    used_in_entity_id?: string;
  }): Promise<MediaUploadResponse> => {
    const response = await apiClient.post('/media/from-external-url', request);
    return response.data;
  },

  // ============================================
  // UTILITY FUNCTIONS
  // ============================================

  /**
   * Check if media is image that can be displayed directly
   */
  isDisplayableImage: (media: MediaAssetSummary): boolean => {
    return media.media_type === 'image' && 
           ['.jpg', '.jpeg', '.png', '.gif', '.webp'].some(ext => 
             media.filename.toLowerCase().endsWith(ext)
           );
  },

  /**
   * Format file size for display
   */
  formatFileSize: (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  }
};

export default mediaApiService;